import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { ToggleButton } from '@/uiKit/atoms/ToggleButton.tsx';
import { MenuTooltip } from '@/uiKit/atoms/floating/MenuTooltip.tsx';
import { AtomProps } from '@/uiKit/atoms/utils/AtomTypes.ts';

const HEADING_SELECT_WIDTH = 110;

export type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'normal';

type HeadingDefinition = {
  type: HeadingType;
  label: string;
};

const headingOptions: Record<HeadingType, HeadingDefinition> = {
  h1: {
    type: 'h1',
    label: 'atoms.richText.h1',
  },
  h2: {
    type: 'h2',
    label: 'atoms.richText.h2',
  },
  h3: {
    type: 'h3',
    label: 'atoms.richText.h3',
  },
  h4: {
    type: 'h4',
    label: 'atoms.richText.h4',
  },
  h5: {
    type: 'h5',
    label: 'atoms.richText.h5',
  },
  h6: {
    type: 'h6',
    label: 'atoms.richText.h6',
  },
  normal: {
    type: 'normal',
    label: 'atoms.richText.normal',
  },
};

export type BaseHeadingSelectButtonProps = {
  heading: HeadingType;
  onSelectHeading: (heading: HeadingType) => void;
};

export type HeadingSelectButtonProps = AtomProps<BaseHeadingSelectButtonProps>;

export const HeadingSelectButton: React.FC<HeadingSelectButtonProps> = ({
  className,
  heading,
  onSelectHeading,
  ...props
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <MenuTooltip
      className={cx(styles.container, className)}
      placement="bottom-start"
      animation={{
        duration: 0,
      }}
      content={
        <div className={styles.options}>
          {Object.values(headingOptions).map(item => (
            <Button
              variant="text"
              key={item.type}
              className={cx({
                [styles.optionButton]: true,
                [styles.optionSelected]:
                  item.type === headingOptions[heading].type,
              })}
              onClick={() => onSelectHeading(item.type)}
            >
              {t(item.label)}
            </Button>
          ))}
        </div>
      }
      click={{ enabled: true }}
      openOnHover={false}
    >
      <ToggleButton
        id="rich-text-editor-heading-select-button"
        value="text-color"
        aria-label="color"
        toggled={false}
        onToggle={() => {}}
        {...props}
      >
        <div className={styles.selection}>
          <Typography variant="body">
            {t(headingOptions[heading].label)}
          </Typography>
          <Icons.arrowDown />
        </div>
      </ToggleButton>
    </MenuTooltip>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  options: css({
    display: 'flex',
    flexDirection: 'column',
  }),

  optionButton: css({
    padding: theme.spacing(2, 4),
    color: theme.palette.text.primary,
    width: HEADING_SELECT_WIDTH,
  }),
  optionSelected: css({
    backgroundColor: theme.palette.background.default,
  }),
  selection: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2),
    width: HEADING_SELECT_WIDTH,
  }),
});
